/** Catálogo para la UI del formulario: nombres llanos, opciones canónicas y tipos de deuda. Los valores guardados SIEMPRE son los del catálogo del modelo. */
import { assetsByCategory, loadCatalog } from "@/engine/catalog";
import type { CatalogAsset } from "@/engine/catalog/types";
import { norm } from "@/engine/excel/fns";

export const CAT = loadCatalog("wx-final2");
export const P = CAT.params;

export const MODALIDADES = P.modalities.map((m) => ({ valor: m.name, etiqueta: m.name }));
export const TIPO_FUENTE = new Map(P.incomeSources.map((s) => [s.source, s.type]));
export const ESTRATEGIAS = P.mixes.map((m) => m.strategy);
export const MESES_FONDO = [
  { valor: 6, etiqueta: "Tengo nómina", ayuda: "3 a 6 meses" },
  { valor: 12, etiqueta: "Soy empresario o mi ingreso varía", ayuda: "12 meses" },
  { valor: 24, etiqueta: "Ya vivo de mi patrimonio", ayuda: "18 a 24 meses" },
];

/** Nombres en lenguaje llano de las 12 categorías (el canónico se muestra en gris). */
export const CATEGORIAS: { canonica: string; llana: string; linea: string }[] = [
  { canonica: P.lists.categories[0], llana: "Efectivo y cuentas", linea: "Banco, débito, fondos de liquidez, efectivo" },
  { canonica: P.lists.categories[1], llana: "Le presté dinero", linea: "CETES, bonos, pagarés, préstamos con intereses" },
  { canonica: P.lists.categories[2], llana: "Bolsa", linea: "Acciones, ETFs, fondos indexados" },
  { canonica: P.lists.categories[3], llana: "Negocios y capital privado", linea: "Tu empresa, private equity, venture capital" },
  { canonica: P.lists.categories[4], llana: "Fondos mixtos", linea: "Alguien invierte por mí en un poco de todo" },
  { canonica: P.lists.categories[5], llana: "Bienes raíces", linea: "Depas en renta, locales, terrenos, FIBRAs" },
  { canonica: P.lists.categories[6], llana: "Oro, metales y commodities", linea: "Oro físico, plata, ETFs de metales" },
  { canonica: P.lists.categories[7], llana: "Cripto", linea: "Bitcoin, Ethereum, stablecoins" },
  { canonica: P.lists.categories[8], llana: "Coleccionables y alternativos", linea: "Arte, relojes, vino, regalías" },
  { canonica: P.lists.categories[9], llana: "Retiro y seguros", linea: "AFORE, PPR, seguros con ahorro" },
  { canonica: P.lists.categories[10], llana: "Uso personal", linea: "La casa donde vives, tu coche" },
  { canonica: P.lists.categories[11], llana: "Otros / no sé qué es", linea: "Préstamos a familia, lo que no encaje" },
];

export const subcategoriasDe = (categoria: string) => assetsByCategory(CAT, categoria);
export const activoPorSub = (sub: string | null): CatalogAsset | undefined => (sub ? CAT.assets.find((a) => norm(a.subcategory) === norm(sub)) : undefined);
export const categoriaLlana = (canonica: string | null) => CATEGORIAS.find((c) => c.canonica === canonica)?.llana ?? canonica ?? "";

/** Atajos "lo más común" (subcategorías canónicas). */
export const COMUNES: { etiqueta: string; sub: string }[] = [
  { etiqueta: "Cuenta de banco", sub: "Cuenta de banco o de débito" },
  { etiqueta: "Casa donde vivo", sub: "Casa o depa donde vivo" },
  { etiqueta: "Coche", sub: "Autos y motos" },
  { etiqueta: "AFORE", sub: "AFORE (cuenta obligatoria)" },
  { etiqueta: "CETES", sub: "CETES" },
  { etiqueta: "ETF indexado", sub: "ETF indexado amplio (S&P 500, mundo)" },
  { etiqueta: "Mi negocio", sub: "Mi empresa o negocio propio" },
].filter((c) => !!activoPorSub(c.sub));

/** Ayuda contextual del valor según la subcategoría. */
export function ayudaValor(a: CatalogAsset | undefined): string {
  if (!a) return "Lo que vale hoy. Aproxima: si dudas, pon el número de en medio.";
  const s = norm(a.subcategory);
  if (s.includes("seguro")) return "El valor de rescate de hoy, no la suma asegurada.";
  if (s.includes("negocio") || s.includes("empresa") || s.includes("franquicia")) return "Lo que te darían si lo vendieras hoy.";
  if (a.category.startsWith("6") || s.includes("casa") || s.includes("depa")) return "Valor de venta hoy (sin restarle la hipoteca: la hipoteca va en deudas).";
  if (s.includes("auto")) return "Lo que te darían si lo vendieras hoy.";
  if (s.includes("prestamo a familiar")) return "Solo lo que de verdad crees que te van a regresar.";
  return "Lo que vale hoy. Aproxima: si dudas, pon el número de en medio.";
}

// ───────────── Tipos de deuda (fijan "¿Deuda de propiedades de uso personal?") ─────────────
export type TipoDeudaId = "hipoteca_casa" | "hipoteca_renta" | "tarjeta" | "auto" | "personal" | "otro";
export const TIPOS_DEUDA: { id: TipoDeudaId; etiqueta: string; personalUse: "Sí" | "No" | null; what: string; explica: string }[] = [
  { id: "hipoteca_casa", etiqueta: "Hipoteca de mi casa", personalUse: "Sí", what: "Hipoteca de mi casa", explica: "Es la única que no se resta de tu patrimonio invertible: tu casa tampoco está ahí." },
  { id: "hipoteca_renta", etiqueta: "Hipoteca de una propiedad que rento", personalUse: "No", what: "Hipoteca de propiedad en renta", explica: "La propiedad sí cuenta como invertible, así que esta deuda sí se resta." },
  { id: "tarjeta", etiqueta: "Tarjeta de crédito", personalUse: "No", what: "Tarjeta de crédito", explica: "Se resta de lo que tienes invertido. (El error más común en el Excel es marcarla como de uso personal.)" },
  { id: "auto", etiqueta: "Crédito de auto", personalUse: "No", what: "Crédito de auto", explica: "Se resta de lo que tienes invertido." },
  { id: "personal", etiqueta: "Préstamo personal", personalUse: "No", what: "Préstamo personal", explica: "Se resta de lo que tienes invertido." },
  { id: "otro", etiqueta: "Otra", personalUse: null, what: "", explica: "Dinos si es la hipoteca de la casa donde vives." },
];

/** Infiere el tipo de deuda de una fila (al sembrar desde Excel o recargar). */
export function inferirTipoDeuda(what: string | null, personalUse: string | null): TipoDeudaId {
  const t = norm(what);
  if (/tarjeta|tdc|credito revolvente/.test(t)) return "tarjeta";
  if (/auto|coche|carro|automotriz|moto/.test(t)) return "auto";
  if (/prestamo|personal|nomina/.test(t)) return "personal";
  if (/hipoteca|credito hipotecario|infonavit|fovissste/.test(t)) return personalUse === "Sí" ? "hipoteca_casa" : /renta|rento|rentad/.test(t) ? "hipoteca_renta" : personalUse === "No" ? "hipoteca_renta" : "hipoteca_casa";
  return "otro";
}
